import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AnimatedText from '../components/AnimatedText';
import Services from '../components/Services';
import WorkTogether from '../components/WorkTogether';
import DirectScrollButton from '../components/DirectScrollButton';
import PageSeparator from '../components/PageSeparator';
import SEO from '../components/SEO';

const Home = ({ fontsReady }) => {
  const { t } = useTranslation();
  const [hoveredButton, setHoveredButton] = useState(false);
  const [animateText, setAnimateText] = useState(false);
  const [expandOffset, setExpandOffset] = useState(2.5);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Only apply expansion on mobile view
      if (window.innerWidth < 768) {
        const maxScroll = 100;
        const initialOffset = 2.5;
        const clamped = Math.min(Math.max(scrollY / maxScroll, 0), 1);
        const dynamicOffset = initialOffset * (1 - clamped);
        setExpandOffset(dynamicOffset);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (fontsReady) {
      const timeout = setTimeout(() => setAnimateText(true), 0);
      return () => clearTimeout(timeout);
    }
  }, [fontsReady]);

  // Set default offset on desktop (static layout)
  const leftRightOffset = windowWidth >= 768 ? '0vw' : `${expandOffset}vw`;

  return (
    <main className="text-gray-900">
      <SEO 
        title="AlzijDigi | Op Maat Gemaakte Software & Digitale Diensten in Nederland"
        description="Transformeer uw bedrijf met AlzijDigi's expert digitale oplossingen. Wij zijn gespecialiseerd in op maat gemaakte software ontwikkeling, webapplicaties en digitale marketing diensten in Nederland."
        canonicalUrl="/"
      />
      <DirectScrollButton />

      <section className="w-full min-h-screen relative overflow-hidden bg-white flex items-center justify-center">
        <div
          className={`absolute top-0 bottom-0 mt-[4rem] md:mt-0 overflow-hidden ${
            windowWidth < 768 && expandOffset > 0 ? 'rounded-t-3xl' : ''
          }`}
          style={{
            left: leftRightOffset,
            right: leftRightOffset,
          }}
        >
          <video
            className="w-full h-full object-cover"
            src="/asset/vid/loopbackground.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="absolute inset-0 backdrop-blur-xl pointer-events-none" />
        </div>
        
        <div className="relative z-10 text-white w-full min-h-screen">
          <div className="w-[98vw] sm:w-[96vw] md:w-[94vw] lg:w-[93vw] xl:w-[97.8vw] min-h-[99vh] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 pt-[15rem] md:pt-12 pb-12 flex flex-col md:flex-row gap-y-10 md:justify-between">
            <div className="md:w-1/2 flex flex-col justify-center mt-1 md:mt-0">
              <div className="text-left text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-0">
                <AnimatedText text={t('collaborate_create')} animate={animateText} />
              </div>
              <p className="text-base md:text-lg lg:text-xl leading-relaxed mt-4 md:mt-6 text-left">
                {t('build_description')}
              </p>
            </div>

            <div className="w-full md:w-1/2 flex flex-col justify-center mt-4 md:mt-0 md:pl-12">
              <h2 className="text-xl md:text-2xl lg:text-4xl mb-4 text-left">
                {t('see_difference')}<span className="inline-block w-1.5 h-1.5 bg-emerald-800 ml-1"></span>
              </h2>
              <button
                className="relative overflow-hidden transition-all duration-300 inline-block mt-2 md:mt-4 self-start"
                onMouseEnter={() => setHoveredButton(true)}
                onMouseLeave={() => setHoveredButton(false)}
                onClick={() => window.location.href = '/cases'}
              >
                <span className="relative z-10 text-black text-base px-4 py-2 inline-block">
                  {t('view_cases')}
                </span>
                <span
                  className="absolute inset-0 bg-white transition-transform duration-300"
                  style={{ transform: hoveredButton ? 'scale(0.85)' : 'scale(1)' }}
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Services />
      <WorkTogether />
      <PageSeparator />
    </main>
  );
};

export default Home;